/** The rich (email) composer's paste decision, kept `platejs`-free so it is unit testable. */

import {
  clipboardHasImageOrFile,
  htmlHasComplexBlockStructure,
  htmlHasInlineImageData,
  htmlLooksLikeSpreadsheetPaste,
  htmlToParagraphHtml,
  plainTextToParagraphLines,
  spreadsheetHtmlToText,
  type PasteClipboardData,
} from "./paste-html-normalize";

/** Editor operations a claimed paste needs, generic over the editor's fragment type. */
export interface RichPasteTarget<TFragment> {
  /** Insert one paragraph per line at the caret. */
  insertParagraphs(lines: string[], options?: { removeEmpty?: boolean }): void;
  /** Deserialize pasted HTML, or `null` when it yields nothing usable. */
  parseHtml(html: string): TFragment | null;
  insertFragment(fragment: TFragment): void;
  focus(): void;
}

export interface RichPasteEvent {
  readonly clipboardData: PasteClipboardData;
  preventDefault(): void;
}

/** Returns whether the paste was claimed; Plate's `isEventHandled` ignores `preventDefault()`, so a falsy return double-pastes. */
export function handleRichPaste<TFragment>(
  event: RichPasteEvent,
  target: RichPasteTarget<TFragment>,
): boolean {
  const { clipboardData } = event;
  if (clipboardHasImageOrFile(clipboardData)) {
    return false;
  }

  const html = clipboardData.getData("text/html");
  if (!html) {
    const text = clipboardData.getData("text/plain");
    const lines = text ? plainTextToParagraphLines(text) : [];
    if (lines.length <= 1) {
      return false;
    }
    event.preventDefault();
    target.insertParagraphs(lines);
    target.focus();
    return true;
  }
  if (htmlHasInlineImageData(html)) {
    return false;
  }

  if (htmlLooksLikeSpreadsheetPaste(html)) {
    const spreadsheetText = spreadsheetHtmlToText(html);
    if (!spreadsheetText) {
      return false;
    }

    event.preventDefault();
    target.insertParagraphs(spreadsheetText.split("\n"), { removeEmpty: true });
    target.focus();
    return true;
  }

  // Rich block structures (lists, tables, headings, …) deserialize correctly
  // through Plate's own plugins; leave those to the default paste path.
  if (htmlHasComplexBlockStructure(html)) {
    return false;
  }

  // Plate collapses `<div>`/`<br>`-separated HTML into one block, so rebuild
  // paragraph boundaries (PRO-957); one paragraph means nothing needed fixing.
  const paragraphs = htmlToParagraphHtml(html);
  if (paragraphs.length <= 1) {
    return false;
  }

  const fragment = target.parseHtml(paragraphs.join(""));
  if (fragment === null) {
    return false;
  }

  event.preventDefault();
  target.insertFragment(fragment);
  target.focus();
  return true;
}
